import { useMemo } from 'react';
import debounce from 'lodash/debounce';

import { useProductOptionContext } from '../../context/ProductOptionProvider';
import { getCustomInputValues } from '../../core/custom-input.core';
import { checkRequiredFieldsFilled } from '../../core/custom-input.core';
import { buildItemCard } from '../../core/item-card.core';
import type {
  CustomInputOption,
  CustomInputProps,
  CustomInputState,
  ProductOptionConfig,
} from '../../type/common.type';
import { showZDSToastInOption } from '../../util/toast/zds-toast-in-option';
import { convertHtmlEntitiesToSpaces } from '../../util/transformers/html-entity-transformer';
import { generateInputPlaceholder } from '../../util/transformers/input-placeholder-transformer';
import { productMetadataBuilder } from '../../util/transformers/product-metadata-transformer';

interface Props {
  options: CustomInputOption[];
  config: ProductOptionConfig;
}

const DEFAULT_LABEL_HEIGHT = 16;

export const useCustomInput = ({ options, config }: Props) => {
  const { state, actions } = useProductOptionContext();
  const product_metadata = productMetadataBuilder(state.option.info, config);
  const emptyMainOption = state.option.info?.catalog_product.product_option_list.length === 0;

  const debouncedToastShow = debounce((msg: string) => showZDSToastInOption(msg), 200);

  const updateInputState = (name: string, updates: Partial<Omit<CustomInputState, 'shadowEl'>>) => {
    actions.updateCustomInputState({ name, updates });
    actions.closeAllDropdowns();
  };

  const addItemCard = () => {
    if (!state.option.info) {
      return;
    }

    actions.addItemCard(
      buildItemCard({
        item_cards: state.item_cards,
        selected_item: state.option.info.catalog_product.matched_item_list[0],
        custom_input_list: getCustomInputValues(state.custom_input_states),
        context: {
          bogo_info: product_metadata.bogo_info,
          maxPurchaseQuantity: config?.maxPurchaseQuantity,
          min_order_quantity: product_metadata.min_order_quantity,
          is_order_quantity_product_type: product_metadata.is_order_quantity_product_type,
          is_order_quantity_item_type: product_metadata.is_order_quantity_item_type,
          is_store_deal_banner: product_metadata.has_store_deal_banner,
          enableSingleItemSelection: config?.enableSingleItemSelection,
        },
      }),
    );
  };

  const customInputsData = useMemo<CustomInputProps[]>(() => {
    return options.map((option) => {
      const inputState = state.custom_input_states.get(option.name);
      const shadowEl = inputState?.shadowEl;
      const inputEl = inputState?.inputEl;
      const labelHeight =
        inputState?.focused && shadowEl?.current ? shadowEl.current.clientHeight : DEFAULT_LABEL_HEIGHT;
      const maxLength = option.max_length ?? 0;
      const placeholder = generateInputPlaceholder({ required: option.is_required });
      const name = convertHtmlEntitiesToSpaces(option.name);

      // state가 없는 경우 기본값 제공
      const safeState = inputState || {
        value: '',
        focused: false,
        inputEl: inputEl || { current: null },
        shadowEl: shadowEl || { current: null },
      };

      return {
        ...safeState,
        name,
        placeholder,
        labelHeight,
        maxLength,
        isRequired: option.is_required,
        handleChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.currentTarget.value;
          if (newValue.length > maxLength) {
            debouncedToastShow(`텍스트는 ${maxLength}자 이하로 입력해 주세요.`);
          } else {
            updateInputState(name, { value: newValue });
          }
        },
        handleFocus: () => {
          updateInputState(name, { focused: true });
        },
        handleBlur: (e: React.SyntheticEvent<HTMLInputElement>) => {
          updateInputState(name, { focused: false });
          // 빈 문자열 blur 시 자동 추가 방지
          if (!e.currentTarget.value) {
            return;
          }
          // 필수 입력형 입력 완료 & 메인 옵션 없는 경우, 아이템 카드 추가
          if (emptyMainOption && checkRequiredFieldsFilled(state.custom_input_states)) {
            addItemCard();
          }
        },
        handleKeydown: (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            // IME 조합 중인지 확인 (한글 입력 시 중복 호출 방지)
            if (e.nativeEvent.isComposing) {
              return;
            }
            // blur()를 호출하면 handleBlur에서 addItemCard()가 실행되므로 여기서는 호출하지 않음
            inputEl?.current?.blur();
          }
        },
      };
    });
  }, [options, state.custom_input_states]);

  return {
    display: customInputsData?.length > 0 && (emptyMainOption || !state.ui.is_item_card_mode_active),
    data: customInputsData,
  };
};
